import React, { createContext, useContext, useMemo, useRef, useCallback, useEffect, ReactNode } from 'react';

// Type for the query cache entry
type CacheEntry<T> = {
  data: T[];
  timestamp: number;
};

// Type for the query cache map
type QueryCache = {
  [queryKey: string]: CacheEntry<any>;
};

// Type for the context value
type QueryCacheContextValue = {
  getQueryData: <T>(queryKey: string) => T[] | undefined;
  setQueryData: <T>(queryKey: string, data: T[]) => void;
  invalidateQuery: (queryKey: string) => void;
  invalidateQueries: (pattern?: RegExp) => void;
};

// Create the context
const QueryCacheContext = createContext<QueryCacheContextValue | undefined>(undefined);

// Cache expiration time (15 minutes in milliseconds)
const CACHE_TTL = 15 * 60 * 1000;

// Maximum number of cache entries to prevent unbounded growth
const MAX_CACHE_ENTRIES = 500;

/**
 * Provider component that makes the query cache available to any nested component.
 */
export function QueryCacheProvider({ children }: { children: ReactNode }) {
  // Ref-backed store to avoid provider re-renders
  const storeRef = useRef<Map<string, CacheEntry<any>>>(new Map());

  const getQueryData = useCallback(<T,>(queryKey: string): T[] | undefined => {
    const entry = storeRef.current.get(queryKey);
    if (!entry) return undefined;
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      storeRef.current.delete(queryKey);
      return undefined;
    }
    return entry.data as T[];
  }, []);

  const setQueryData = useCallback(<T,>(queryKey: string, data: T[]) => {
    // LRU eviction if we exceed max entries
    if (storeRef.current.size >= MAX_CACHE_ENTRIES) {
      // Find and remove oldest entries (by timestamp)
      const entries = Array.from(storeRef.current.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      // Remove oldest 10% to avoid frequent evictions
      const toRemove = Math.max(1, Math.floor(MAX_CACHE_ENTRIES * 0.1));
      for (let i = 0; i < toRemove && i < entries.length; i++) {
        storeRef.current.delete(entries[i][0]);
      }
    }
    
    storeRef.current.set(queryKey, { data, timestamp: Date.now() });
  }, []);

  const invalidateQuery = useCallback((queryKey: string) => {
    storeRef.current.delete(queryKey);
  }, []);

  const invalidateQueries = useCallback((pattern?: RegExp) => {
    if (!pattern) {
      storeRef.current.clear();
      return;
    }
    for (const key of Array.from(storeRef.current.keys())) {
      if (pattern.test(key)) storeRef.current.delete(key);
    }
  }, []);

  // Background TTL cleanup for long-lived sessions
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      for (const [k, e] of storeRef.current) {
        if (now - e.timestamp > CACHE_TTL) storeRef.current.delete(k);
      }
    }, 60_000); // Clean every minute
    return () => clearInterval(id);
  }, []);

  const value: QueryCacheContextValue = useMemo(() => ({
    getQueryData,
    setQueryData,
    invalidateQuery,
    invalidateQueries,
  }), [getQueryData, setQueryData, invalidateQuery, invalidateQueries]);

  return (
    <QueryCacheContext.Provider value={value}>
      {children}
    </QueryCacheContext.Provider>
  );
}

/**
 * Hook to access the query cache context.
 * Throws an error if used outside of a QueryCacheProvider.
 */
export function useQueryCache() {
  const context = useContext(QueryCacheContext);
  if (context === undefined) {
    throw new Error('useQueryCache must be used within a QueryCacheProvider');
  }
  return context;
}